import type { LeagueSummary } from "@softmax/observatory-client/types";
import { gameBrief, leagueTag } from "@/lib/leagues/watch";
import { curatedLeagueName, curatedText } from "@softmax/web-features/coworlds";
import { curatedPitch } from "../pitches";

const socialDescriptionMax = 300;

// Attribute escaping counts toward the social description's serialized length.
const htmlEscaped: Record<string, number> = {
  "&": 5,
  "<": 4,
  ">": 4,
  '"': 6,
  "'": 6,
};

function escapedLength(text: string): number {
  let total = 0;

  for (const char of text) total += htmlEscaped[char] ?? char.length;

  return total;
}

export function playerNotPolicy(text: string): string {
  return text.replace(/(?<!privacy\s)\bpolic(y|ies)\b/gi, (match, suffix) => {
    const replacement = suffix.toLowerCase() === "ies" ? "players" : "player";
    const firstChar = match.charAt(0);
    return firstChar === firstChar.toUpperCase() &&
      firstChar !== firstChar.toLowerCase()
      ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
      : replacement;
  });
}

export function socialDescription(league: LeagueSummary): string {
  const pitch = curatedPitch(league.name);

  if (pitch) return pitch;

  const brief = gameBrief(league);
  const premise = brief ? playerNotPolicy(curatedText(brief.premise)) : null;

  if (
    premise &&
    /[.!?]$/.test(premise) &&
    escapedLength(premise) <= socialDescriptionMax
  ) {
    return premise;
  }

  return `Watch the top two players in ${curatedLeagueName(league)} play ${leagueTag(league)} on Softmax.`;
}

export function stripLeagueName({
  sentence,
  name,
}: {
  readonly sentence: string;
  readonly name: string;
}): string {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const label = new RegExp(`^${escaped}\\s*[:—-]\\s*`, "i");
  const withoutLabel = sentence.replace(label, "").trim();
  // Only remove a leading name; an interior match can change the game being described.
  const atHead = new RegExp(`^(?:the\\s+)?${escaped}\\b(?!\\s+v?\\d)\\s*`, "i");
  const stripped = withoutLabel.replace(atHead, "").trim();
  const grammatical =
    stripped.length >= 20 &&
    !/^(?:and|or|but|plus|with)\b/i.test(stripped) &&
    !/^[({[]/.test(stripped) &&
    !/^[,;:]/.test(stripped);
  const kept = grammatical ? stripped : withoutLabel;
  return kept
    .replace(/^[\s,;:—-]+/, "")
    .replace(/\s*[.]$/, "")
    .replace(/^([a-z])/, (c) => c.toUpperCase())
    .trim();
}
